import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import FilterDateInput from 'shared/components/filters/FilterDateInput';
import PropertyMultiselectInput from 'shared/components/filters/PropertyMultiselectInput';
import {
  VALUE_PAGE_SIZE as PAGE_SIZE,
  activateTeam,
  isAbortError,
  loadValues,
} from 'shared/utils/filters/propertyCatalogStore';

/** Pause after the last keystroke before the server is asked for matching values. */
export const VALUE_SEARCH_DEBOUNCE_MS = 250;

const TIME_UNITS = ['minutes', 'hours', 'days', 'weeks', 'months', 'years'];

/* ------------------------------------------------------------------ */
/* Operator catalog (1:1 with EventsFilterModule)                      */
/* ------------------------------------------------------------------ */
export const STRING_OPS = [
  { value: 'is',                label: 'Is' },
  { value: 'is_not',            label: 'Is not' },
  { value: 'contains',          label: 'Contains' },
  { value: 'does_not_contain',  label: 'Does not contain' },
  { value: 'is_set',            label: 'Is set' },
  { value: 'is_not_set',        label: 'Is not set' },
];
export const NUMBER_OPS = [
  { value: 'equal',              label: 'Equals' },
  { value: 'not_equal',          label: 'Not equal' },
  { value: 'greater_than',       label: 'Greater than' },
  { value: 'greater_than_equal', label: 'Greater than or equal to' },
  { value: 'less_than',          label: 'Less than' },
  { value: 'less_than_equal',    label: 'Less than or equal to' },
  { value: 'between',            label: 'Between' },
  { value: 'not_between',        label: 'Not between' },
  { value: 'is_numeric',         label: 'Is numeric' },
  { value: 'is_not_numeric',     label: 'Is not numeric' },
];
export const BOOL_OPS = [
  { value: 'is_true',  label: 'Is true' },
  { value: 'is_false', label: 'Is false' },
];
export const DATETIME_OPS = [
  { value: 'last',             label: 'Last' },
  { value: 'not_in_the_last',  label: 'Not in the last' },
  { value: 'between',          label: 'Between' },
  { value: 'not_between',      label: 'Not between' },
  { value: 'on',               label: 'On' },
  { value: 'not_on',           label: 'Not on' },
  { value: 'before_the_last',  label: 'Before the last' },
  { value: 'before',           label: 'Before' },
  { value: 'since',            label: 'Since' },
];

export const LIST_MODE_OPS = [
  { value: 'any_in_list', label: 'Any in list' },
  { value: 'all_in_list', label: 'All in list' },
];

export function getOperatorsForType(dataType) {
  switch (dataType) {
    case 'number':   return NUMBER_OPS;
    case 'boolean':  return BOOL_OPS;
    case 'datetime': return DATETIME_OPS;
    case 'string':   return STRING_OPS;
    default:         return STRING_OPS;
  }
}

export function defaultOperatorForType(dataType) {
  return getOperatorsForType(dataType)[0]?.value || 'is';
}

const VALUELESS_OPS = new Set([
  'is_set', 'is_not_set', 'is_true', 'is_false', 'is_numeric', 'is_not_numeric',
]);
export const isValuelessOp = (op) => VALUELESS_OPS.has(op);

function sampleValueToOption(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'object' && v !== null && 'value' in v) {
    return { value: v.value, label: v.label ?? String(v.value) };
  }
  return { value: String(v), label: String(v) };
}

/* ------------------------------------------------------------------ */
/* Value editor                                                        */
/* ------------------------------------------------------------------ */
function NumInput({ value, onChange, placeholder }) {
  return (
    <input
      type="number"
      value={value == null ? '' : value}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') onChange('');
        else if (Number.isFinite(Number(raw))) onChange(Number(raw));
        else onChange(raw);
      }}
      placeholder={placeholder || 'value'}
      className="w-24 rounded border border-[color:var(--border-default)] bg-surface px-1.5 py-0.5 text-[12px] focus:outline-none"
    />
  );
}
function TextInput({ value, onChange, placeholder, autoFocus }) {
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || 'value'}
      autoFocus={autoFocus}
      className="w-40 rounded border border-[color:var(--border-default)] bg-surface px-1.5 py-0.5 text-[12px] focus:outline-none"
    />
  );
}
function DateTimeInput({ value, onChange }) {
  return (
    <FilterDateInput
      value={value}
      onChange={onChange}
      inputClassName="rounded border border-[color:var(--border-default)] bg-surface px-1.5 py-0.5 text-[12px] text-[color:var(--text-primary)] focus:outline-none"
    />
  );
}
function UnitSelect({ value, onChange }) {
  return (
    <div style={{ minWidth: 84, width: 'max-content' }}>
      <BrandDropdown
        value={value || 'days'}
        onChange={onChange}
        options={TIME_UNITS.map((u) => ({ value: u, label: u }))}
        compact
        menuMinWidth={120}
      />
    </div>
  );
}

/**
 * Render the value editor for a given filter (driven by dataType + operator).
 * Mirrors EventsFilterModule lines 2218-2298 — same operator semantics.
 */
export default function FilterValueEditor({ filter, onUpdate, teamId, agentName }) {
  const { dataType, operator, sampleValues, field } = filter;
  // Values are not fetched until the dropdown is first opened: a chip sitting
  // on the page (or restored from the URL) costs nothing. Opening loads one
  // page; typing asks the server; "Load more" fetches the next page.
  const [opened, setOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [list, setList] = useState({ q: '', items: [], hasMore: false, loading: false, loadingMore: false });
  const moreAbort = useRef(null);

  const scopeAgent = agentName || filter.agentName;
  const serverSearchEnabled = Boolean(teamId && field);
  const isSetOp = operator === 'is' || operator === 'is_not';
  const q = searchQuery.trim();

  const embeddedOpts = useMemo(
    () => (sampleValues || []).map(sampleValueToOption).filter(Boolean),
    [sampleValues],
  );

  const hasEmbedded = embeddedOpts.length > 0;

  // First page for the current query (browse when q is empty, search otherwise).
  useEffect(() => {
    if (!serverSearchEnabled || !isSetOp || !opened) return undefined;
    // Values the filter already carries are the list until the user searches.
    if (!q && hasEmbedded) return undefined;
    activateTeam(teamId);
    const ac = new AbortController();
    moreAbort.current?.abort();
    const run = () => {
      setList({ q, items: [], hasMore: false, loading: true, loadingMore: false });
      loadValues({ teamId, agentName: scopeAgent, field, q, limit: PAGE_SIZE, signal: ac.signal })
        .then((values) => {
          if (ac.signal.aborted) return;
          setList({
            q,
            items: values.map(sampleValueToOption).filter(Boolean),
            hasMore: values.length >= PAGE_SIZE,
            loading: false,
            loadingMore: false,
          });
        })
        .catch((err) => {
          if (isAbortError(err) || ac.signal.aborted) return;
          setList({ q, items: [], hasMore: false, loading: false, loadingMore: false });
        });
    };
    // Typing is debounced; opening the dropdown is not.
    const timer = setTimeout(run, q ? VALUE_SEARCH_DEBOUNCE_MS : 0);
    if (q) setList((l) => ({ ...l, q, loading: true }));
    return () => { clearTimeout(timer); ac.abort(); };
  }, [serverSearchEnabled, isSetOp, opened, q, hasEmbedded, teamId, field, scopeAgent]);

  const loadMoreValues = useCallback(() => {
    if (list.loading || list.loadingMore || !list.hasMore) return;
    const ac = new AbortController();
    moreAbort.current?.abort();
    moreAbort.current = ac;
    const forQ = list.q;
    setList((l) => ({ ...l, loadingMore: true }));
    loadValues({ teamId, agentName: scopeAgent, field, q: forQ, offset: list.items.length, limit: PAGE_SIZE, signal: ac.signal })
      .then((values) => {
        if (ac.signal.aborted) return;
        setList((l) => {
          if (l.q !== forQ) return l;
          const seen = new Set(l.items.map((o) => o.value));
          const fresh = values.map(sampleValueToOption).filter((o) => o && !seen.has(o.value));
          return { ...l, items: l.items.concat(fresh), hasMore: values.length >= PAGE_SIZE, loadingMore: false };
        });
      })
      .catch((err) => {
        if (isAbortError(err) || ac.signal.aborted) return;
        setList((l) => ({ ...l, hasMore: false, loadingMore: false }));
      });
  }, [list, teamId, scopeAgent, field]);

  useEffect(() => () => moreAbort.current?.abort(), []);

  const handleSearchChange = useCallback((next) => setSearchQuery(next), []);
  const handleOpenChange = useCallback((isOpen) => { if (isOpen) setOpened(true); }, []);

  if (isValuelessOp(operator)) return null;

  // string -------------------------------------------------------------
  if (dataType === 'string' || !dataType) {
    if (operator === 'is' || operator === 'is_not') {
      // Values the filter already carries (an older link) show without a fetch
      // until the user searches; otherwise the list is whatever the server sent.
      const pageOpts = (!q && hasEmbedded) ? embeddedOpts : list.items;
      const selected = Array.isArray(filter.value)
        ? filter.value
        : (filter.value == null || filter.value === '' ? [] : [filter.value]);
      // Picked values stay on the list (at the top) even when the page the
      // server sent does not hold them, so they can always be unticked.
      const onPage = new Set(pageOpts.map((o) => o.value));
      const ql = q.toLowerCase();
      const pinned = selected
        .filter((v) => !onPage.has(v) && (!ql || String(v).toLowerCase().includes(ql)))
        .map((v) => ({ value: v, label: String(v) }));
      const displayOpts = pinned.length ? pinned.concat(pageOpts) : pageOpts;
      const browsing = !q;
      return (
        <PropertyMultiselectInput
          value={selected}
          onChange={(arr) => onUpdate({ value: arr })}
          options={displayOpts}
          placeholder="Select values"
          onSearchChange={serverSearchEnabled ? handleSearchChange : undefined}
          onOpenChange={serverSearchEnabled ? handleOpenChange : undefined}
          serverSearch={serverSearchEnabled}
          searchLoading={list.loading && !(browsing && hasEmbedded)}
          allowCustomValue={serverSearchEnabled}
          hasMore={list.hasMore && !(browsing && hasEmbedded)}
          loadingMore={list.loadingMore}
          onLoadMore={loadMoreValues}
        />
      );
    }
    // contains / does_not_contain / starts_with / ends_with
    return (
      <TextInput
        value={filter.value ?? ''}
        onChange={(v) => onUpdate({ value: v })}
        autoFocus={filter._justAdded}
      />
    );
  }

  // number -------------------------------------------------------------
  if (dataType === 'number') {
    if (operator === 'between' || operator === 'not_between') {
      return (
        <span className="inline-flex items-center gap-1.5">
          <NumInput value={filter.value} onChange={(v) => onUpdate({ value: v })} placeholder="from" />
          <span className="text-[11px] text-[color:var(--text-muted)]">to</span>
          <NumInput value={filter.value2} onChange={(v) => onUpdate({ value2: v })} placeholder="to" />
        </span>
      );
    }
    return (
      <NumInput
        value={filter.value}
        onChange={(v) => onUpdate({ value: v })}
        placeholder="value"
      />
    );
  }

  // datetime -----------------------------------------------------------
  if (dataType === 'datetime') {
    if (operator === 'last' || operator === 'not_in_the_last' || operator === 'before_the_last') {
      return (
        <span className="inline-flex items-center gap-1.5">
          <NumInput
            value={filter.value}
            onChange={(v) => onUpdate({ value: v })}
            placeholder="N"
          />
          <UnitSelect value={filter.value2} onChange={(v) => onUpdate({ value2: v })} />
          {operator === 'before_the_last' ? (
            <span className="text-[11px] text-[color:var(--text-muted)]">ago</span>
          ) : null}
        </span>
      );
    }
    if (operator === 'between' || operator === 'not_between') {
      return (
        <span className="inline-flex items-center gap-1.5">
          <DateTimeInput value={filter.value} onChange={(v) => onUpdate({ value: v })} />
          <span className="text-[11px] text-[color:var(--text-muted)]">to</span>
          <DateTimeInput value={filter.value2} onChange={(v) => onUpdate({ value2: v })} />
        </span>
      );
    }
    // on / not_on / before / since
    return <DateTimeInput value={filter.value} onChange={(v) => onUpdate({ value: v })} />;
  }

  // boolean has no value editor (handled by isValuelessOp).
  // Fallback to plain text.
  return (
    <TextInput
      value={filter.value ?? ''}
      onChange={(v) => onUpdate({ value: v })}
      autoFocus={filter._justAdded}
    />
  );
}
